// Right-rail inspector for a drawer (Cabinet.drawers[i]). Mounted by
// ProjectEditorPage when the outliner's secondary selection points
// to a drawer. Fields mirror what DrawersSection shows in the cabinet
// card, just blown up to a full-rail form with a back arrow to the
// parent cabinet.

import type { Cabinet, Drawer, Id } from "@/lib/types";
import { BLUM_SLIDE_DEPTHS } from "@/lib/cabinets/drawers";
import { getCabinetClass } from "@/lib/cabinets";

interface Props {
  cabinet: Cabinet;
  drawer: Drawer;
  /** Patch drawers[*] in-place — caller propagates to the project. */
  onChange: (patch: Partial<Drawer>) => void;
  onDelete: () => void;
  /** Back to the parent cabinet's inspector. */
  onSelectCabinet: (id: Id) => void;
}

export function DrawerInspector({
  cabinet, drawer, onChange, onDelete, onSelectCabinet,
}: Props) {
  const cls = getCabinetClass(cabinet.classId);
  // Same derivation as DrawersSection — read-only here.
  const T = 0.75;
  const slideAllow = 0.5;
  const interiorW = Math.max(0, cabinet.width - 2 * T - 2 * slideAllow);

  return (
    <aside style={{ borderLeft: "1px solid var(--line)", padding: 16, overflowY: "auto" }}>
      <button
        className="ghost"
        style={{ fontSize: 11, padding: "2px 8px", marginBottom: 8 }}
        onClick={() => onSelectCabinet(cabinet._id)}
      >← {cls?.label ?? "Cabinet"} #{cabinet._id}</button>
      <div className="label">DRAWER</div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
        {drawer.label ?? `Drawer #${drawer._id}`}
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        ID #{drawer._id} · inside cabinet #{cabinet._id}
      </div>

      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 10, alignItems: "center", fontSize: 12 }}>
        <span className="muted">Label</span>
        <input
          type="text"
          value={drawer.label ?? ""}
          placeholder={`Drawer #${drawer._id}`}
          onChange={(e) => onChange({ label: e.target.value || undefined })}
          style={{ fontSize: 12 }}
        />

        <span className="muted">Height ″</span>
        <input
          type="number" step={0.25} min={2} max={24}
          value={drawer.height}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v) && v > 0) onChange({ height: v });
          }}
          style={{ fontSize: 12 }}
        />

        <span className="muted">Y from bottom ″</span>
        <input
          type="number" step={0.25} min={0}
          value={drawer.y}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v) && v >= 0) onChange({ y: v });
          }}
          style={{ fontSize: 12 }}
        />

        <span className="muted">Blum slide depth</span>
        <select
          value={drawer.slideDepthMm}
          onChange={(e) => onChange({ slideDepthMm: Number(e.target.value) })}
          style={{ fontSize: 12 }}
        >
          {BLUM_SLIDE_DEPTHS.map((opt) => (
            <option key={opt.depthMm} value={opt.depthMm}>{opt.label}</option>
          ))}
        </select>

        <span className="muted">Interior width</span>
        <span style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          {interiorW.toFixed(2)}″ · auto (cabinet − sides − slide allowance)
        </span>

        <span className="muted">Face Y span</span>
        <span style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          {drawer.y.toFixed(2)}″ → {(drawer.y + drawer.height).toFixed(2)}″
        </span>
      </div>

      <button
        className="ghost"
        onClick={onDelete}
        style={{ marginTop: 24, color: "var(--danger)", borderColor: "var(--danger)" }}
      >
        Delete drawer
      </button>
    </aside>
  );
}
